import React, { Component } from "react";
import { connect } from "react-redux";
import { I18n } from "react-redux-i18n";
import auth, { get } from "../js/auth";
import admin from "../js/admin";
import SubmenuHeader from "../components/menus/SubMenuHeader";
import AICommandBox from "../js/ai/AICommandBox";

class Customers extends Component {
  t = (key, fallback, ...args) => {
    const translated = I18n.t(key, ...args);
    if (!translated || translated === key) {
      return fallback;
    }
    return translated;
  };

  state = {
    customers: [],
    labels: [],
    search: "",
    loading: false,
    currentPage: 1,
    pageSize: 20,
    totalPages: 1,
    totalElements: 0,
    editingCustomerId: null,
    editingCustomer: null,
    importPreview: [],
    importRawPreview: [],
    importFile: null,
    importHeaders: [],
    importTotalRows: 0,
    importDuplicateCount: 0,
    importDuplicateAction: "skip",
    importMapping: {},
    importFallbackMapping: {},
    importProgress: {
      running: false,
      percent: 0,
      processed: 0,
      total: 0,
      imported: 0,
      updated: 0,
      skipped: 0,
      message: "",
      error: "",
    },
  };

  componentDidMount() {
    this.loadLabels();
    this.loadCustomers();
  }

  loadLabels = () => {
    admin.getLabels(data => this.setState({ labels: Array.isArray(data) ? data : [] }));
  };

  loadCustomers = () => {
    this.setState({ loading: true });
    const contains = this.state.search.trim();
    const page = Math.max(0, this.state.currentPage - 1);
    const size = this.state.pageSize;
    admin.getUsersWithoutEatwithmeRole(contains, page, size, data => {
      const customers = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
      const totalPages = data && typeof data.totalPages === "number" ? data.totalPages : Math.max(1, Math.ceil(customers.length / size));
      const totalElements = data && typeof data.totalElements === "number" ? data.totalElements : customers.length;
      this.setState({ customers, totalPages, totalElements, loading: false });
    }, () => this.setState({ loading: false }));
  };

  handleDelete = customer => {
    const customerName = [customer.name, customer.surname].filter(Boolean).join(" ") || customer.email || `#${customer.id}`;
    if (!window.confirm(I18n.t("admin_local.confirm_customer_deletion", customerName))) {
      return;
    }

    get("adminService/" + localStorage.restaurantSelected + "/deleteCustomer/" + customer.id).done(() => {
      this.loadCustomers();
    });
  };

  startEditCustomer = customer => {
    this.setState({
      editingCustomerId: customer.id,
      editingCustomer: {
        id: customer.id,
        name: customer.name || "",
        surname: customer.surname || "",
        email: customer.email || "",
        phone: this.getPhone(customer) === "-" ? "" : this.getPhone(customer),
        address: this.getStreetAddress(customer) === "-" ? "" : this.getStreetAddress(customer),
        city: this.getCity(customer) === "-" ? "" : this.getCity(customer),
        postcode: this.getPostcode(customer) === "-" ? "" : this.getPostcode(customer),
        country: this.getCountry(customer) === "-" ? "" : this.getCountry(customer),
      },
    });
  };

  cancelEditCustomer = () => {
    this.setState({ editingCustomerId: null, editingCustomer: null });
  };

  handleEditFieldChange = event => {
    const { name, value } = event.target;
    this.setState(prevState => ({
      editingCustomer: {
        ...prevState.editingCustomer,
        [name]: value,
      },
    }));
  };

  saveCustomerEdit = () => {
    const { editingCustomer } = this.state;
    if (!editingCustomer || !editingCustomer.id) {
      return;
    }

    const payload = {
      id: editingCustomer.id,
      name: editingCustomer.name || "",
      surname: editingCustomer.surname || "",
      email: editingCustomer.email || "",
      phone: editingCustomer.phone || "",
      address: editingCustomer.address || "",
      city: editingCustomer.city || "",
      postcode: editingCustomer.postcode || "",
      country: editingCustomer.country || "",
    };

    this.setState({ loading: true });
    admin.updateCustomer(payload, () => {
      this.setState({ loading: false, editingCustomerId: null, editingCustomer: null }, () => this.loadCustomers());
    }, () => this.setState({ loading: false }));
  };

  getStreetAddress = customer => customer.address || customer.street || customer.addressLine || "-";

  getCity = customer => customer.city || customer.town || "-";

  getPostcode = customer => customer.postcode || "-";

  getCountry = customer => customer.country || "-";

  getAddress = customer => {
    const parts = [this.getStreetAddress(customer), this.getCity(customer), this.getPostcode(customer), this.getCountry(customer)].filter(value => value && value !== "-");
    return parts.length ? parts.join(", ") : "-";
  };

  getPhone = customer => customer.phone || customer.mobilePhone || customer.mobile_phone || customer.phoneNumber || "-";

  getLabels = customer => {
    if (!customer.labels || !customer.labels.length) {
      return "-";
    }

    return customer.labels.map(label => label.name || label.label || label).join(", ");
  };

  handleSearchChange = event => {
    this.setState({ search: event.target.value, currentPage: 1 }, () => this.loadCustomers());
  };

  handlePageChange = page => {
    const { totalPages } = this.state;
    const maxPage = Math.max(1, totalPages || 1);
    const safePage = Math.min(Math.max(page, 1), maxPage);
    this.setState({ currentPage: safePage }, () => this.loadCustomers());
  };

  handlePageSizeChange = event => {
    const pageSize = Number(event.target.value) || 20;
    this.setState({ pageSize, currentPage: 1 }, () => this.loadCustomers());
  };

  normalizeImportHeader = header => {
    if (header === null || header === undefined) {
      return "";
    }
    return String(header)
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  };

  getImportHeaderLabel = header => {
    const normalized = this.normalizeImportHeader(header);
    const aliasToTarget = {
      name: "name",
      full_name: "name",
      customer_name: "name",
      first_name: "name",
      firstname: "name",
      given_name: "name",
      nev: "name",
      surname: "surname",
      last_name: "surname",
      lastname: "surname",
      family_name: "surname",
      phone: "phone",
      mobile: "phone",
      mobile_phone: "phone",
      phone_number: "phone",
      tel: "phone",
      telefon: "phone",
      town: "city",
      city: "city",
      postcode: "postcode",
      zip: "postcode",
      zip_code: "postcode",
      postal_code: "postcode",
      address: "address",
      street: "address",
      street_address: "address",
      address_line: "address",
      country: "country",
      nation: "country",
      email: "email",
      e_mail: "email",
      labels: "labels",
      tags: "labels",
    };

    const target = aliasToTarget[normalized];
    if (!target) {
      return header;
    }
    return this.t(`admin_local.customer_table.${target}`, header);
  };

  getImportTargetOptions = () => [
    { value: "name", label: this.t("admin_local.customer_table.name", "Name") },
    { value: "email", label: this.t("admin_local.customer_table.email", "Email") },
    { value: "phone", label: this.t("admin_local.customer_table.phone", "Phone") },
    { value: "postcode", label: this.t("admin_local.customer_table.postcode", "Postcode") },
    { value: "town", label: this.t("admin_local.customer_table.city", "Town") },
    { value: "address", label: this.t("admin_local.customer_table.address", "Address") },
    { value: "country", label: this.t("admin_local.customer_table.country", "Country") },
    { value: "surname", label: this.t("admin_local.customer_table.surname", "Surname") },
    { value: "labels", label: this.t("admin_local.customer_table.labels", "Labels") },
  ];

  getImportSourceOptions = headers => {
    return Array.isArray(headers) ? [...headers] : [];
  };

  getImportPreviewHeaders = (discoveredHeaders = [], rows = []) => {
    const orderedHeaders = [];
    const seen = new Set();
    const addHeader = header => {
      if (header === null || header === undefined) {
        return;
      }
      const cleanHeader = String(header).trim();
      if (!cleanHeader || seen.has(cleanHeader)) {
        return;
      }
      seen.add(cleanHeader);
      orderedHeaders.push(cleanHeader);
    };

    (Array.isArray(discoveredHeaders) ? discoveredHeaders : []).forEach(addHeader);

    const rowKeys = Array.isArray(rows) ? rows.reduce((allKeys, row) => {
      if (row && typeof row === "object") {
        Object.keys(row).forEach(key => allKeys.push(key));
      }
      return allKeys;
    }, []) : [];
    rowKeys.forEach(addHeader);

    ["town", "postcode", "country"].forEach(field => {
      if (!seen.has(field)) {
        addHeader(field);
      }
    });

    return orderedHeaders;
  };

  isImportTargetAllowed = targetField => targetField && targetField !== "id" && !["id"].includes(targetField);

  invertImportMapping = mapping => {
    const inverse = {};
    Object.entries(mapping || {}).forEach(([sourceHeader, targetField]) => {
      if (!sourceHeader || !targetField) {
        return;
      }
      inverse[targetField] = sourceHeader;
    });
    return inverse;
  };

  getDefaultImportMapping = previewRows => {
    const previewColumns = previewRows && previewRows.length ? Object.keys(previewRows[0]) : [];
    const mapping = {};
    previewColumns.forEach(header => {
      const normalized = this.normalizeImportHeader(header);
      let target = "";
      if (["name", "full_name", "customer_name", "first_name", "firstname", "given_name"].includes(normalized)) {
        target = "name";
      } else if (["surname", "last_name", "family_name", "lastname"].includes(normalized)) {
        target = "surname";
      } else if (["email", "email_address", "e_mail", "e_mail_address"].includes(normalized)) {
        target = "email";
      } else if (["phone", "phone_number", "mobile_phone", "mobile", "telephone", "tel"].includes(normalized)) {
        target = "phone";
      } else if (["address", "street", "address_line", "address1", "address_1"].includes(normalized)) {
        target = "address";
      } else if (["city", "town", "municipality"].includes(normalized)) {
        target = "town";
      } else if (["postcode", "zip", "postal_code", "postalcode"].includes(normalized)) {
        target = "postcode";
      } else if (["country", "nation", "orszag"].includes(normalized)) {
        target = "country";
      } else if (["label", "labels"].includes(normalized)) {
        target = "labels";
      }
      if (target && !mapping[target]) {
        mapping[target] = header;
      }
    });
    if (mapping.address) {
      ["town", "postcode", "country"].forEach(targetField => {
        if (!mapping[targetField]) {
          mapping[targetField] = mapping.address;
        }
      });
    }
    return mapping;
  };

  getDefaultImportFallbackMapping = (headers = [], mapping = {}) => {
    return {};
  };

  getImportPreviewCellValue = (row, sourceHeader) => {
    if (!row || !sourceHeader) {
      return "-";
    }
    const mappedField = this.state.importMapping && this.state.importMapping[sourceHeader];
    if (mappedField) {
      const mappedValue = row[mappedField];
      if (mappedValue !== undefined && mappedValue !== null && mappedValue !== "") {
        return mappedValue;
      }
    }
    if (row[sourceHeader] !== undefined && row[sourceHeader] !== null && row[sourceHeader] !== "") {
      return row[sourceHeader];
    }
    return "-";
  };

  getImportPreviewRows = (mappedRows = [], rawRows = []) => {
    const safeMappedRows = Array.isArray(mappedRows) ? mappedRows : [];
    const safeRawRows = Array.isArray(rawRows) ? rawRows : [];
    return safeMappedRows.length ? safeMappedRows : safeRawRows;
  };

  resolveImportMapping = (headers, defaultMapping = {}) => {
    const nextMapping = {};
    Object.entries(defaultMapping || {}).forEach(([targetField, sourceHeader]) => {
      const normalizedTarget = this.normalizeImportHeader(targetField);
      const normalizedKey = this.normalizeImportHeader(sourceHeader);
      const actualHeader = (headers || []).find(header => this.normalizeImportHeader(header) === normalizedKey) || sourceHeader;
      if (normalizedTarget && normalizedTarget !== "") {
        nextMapping[normalizedTarget] = actualHeader;
      }
    });
    return nextMapping;
  };

  handleImportMappingChange = (targetField, sourceHeader) => {
    if (!this.isImportTargetAllowed(targetField)) {
      sourceHeader = "";
    }

    this.setState(prevState => {
      const nextMapping = { ...prevState.importMapping };
      const previousSourceHeader = prevState.importMapping[targetField];
      const nextFallbackMapping = { ...prevState.importFallbackMapping };

      if (previousSourceHeader && prevState.importFallbackMapping[targetField] === previousSourceHeader) {
        delete nextFallbackMapping[targetField];
      }

      if (sourceHeader && sourceHeader !== "") {
        nextMapping[targetField] = sourceHeader;
      } else {
        delete nextMapping[targetField];
      }

      if (previousSourceHeader && (!sourceHeader || sourceHeader !== previousSourceHeader)) {
        delete nextFallbackMapping[targetField];
      }

      const { importFile } = prevState;
      if (importFile) {
        this.refreshImportPreview(importFile, nextMapping, nextFallbackMapping);
      }
      return { importMapping: nextMapping, importFallbackMapping: nextFallbackMapping };
    });
  };

  handleImportFallbackMappingChange = (targetField, fallbackSourceHeader) => {
    if (!this.isImportTargetAllowed(targetField)) {
      return;
    }

    this.setState(prevState => {
      const nextFallbackMapping = { ...prevState.importFallbackMapping };
      const primarySourceHeader = prevState.importMapping[targetField];

      if (fallbackSourceHeader && fallbackSourceHeader !== "") {
        if (primarySourceHeader && fallbackSourceHeader === primarySourceHeader) {
          delete nextFallbackMapping[targetField];
        } else {
          nextFallbackMapping[targetField] = fallbackSourceHeader;
        }
      } else {
        delete nextFallbackMapping[targetField];
      }

      const { importFile } = prevState;
      if (importFile) {
        this.refreshImportPreview(importFile, prevState.importMapping, nextFallbackMapping);
      }

      return { importFallbackMapping: nextFallbackMapping };
    });
  };

  refreshImportPreview = (file, mapping = this.state.importMapping, fallbackMapping = this.state.importFallbackMapping) => {
    if (!file) {
      return;
    }
    this.setState({ loading: true });
    admin.previewCustomers(file, mapping, { fallbackMapping }, data => {
      const previewRows = Array.isArray(data?.sampleRows) ? data.sampleRows : [];
      const rawPreviewRows = Array.isArray(data?.rawRows) ? data.rawRows : [];
      const effectivePreviewRows = this.getImportPreviewRows(previewRows, rawPreviewRows);
      const discoveredHeaders = this.getImportPreviewHeaders(Array.isArray(data?.headers) ? data.headers : [], previewRows);
      const activeMapping = mapping && Object.keys(mapping).length ? mapping : ((data && data.defaultMapping && Object.keys(data.defaultMapping).length)
        ? this.resolveImportMapping(discoveredHeaders, data.defaultMapping)
        : this.getDefaultImportMapping(effectivePreviewRows));
      const inferredAddressSource = activeMapping && (activeMapping.address || Object.keys(activeMapping).find(targetField => activeMapping[targetField] === "Address" || activeMapping[targetField] === "address"));
      const generatedAddressTargets = ["town", "postcode", "country"];
      if (inferredAddressSource) {
        generatedAddressTargets.forEach(targetField => {
          if (!activeMapping[targetField]) {
            const hasDerivedValue = previewRows.some(row => row && row[targetField] !== undefined && row[targetField] !== null && String(row[targetField]).trim() !== "");
            if (hasDerivedValue || targetField === "town" || targetField === "postcode" || targetField === "country") {
              activeMapping[targetField] = inferredAddressSource;
            }
          }
        });
      }
      const activeFallbackMapping = Object.keys(fallbackMapping || {}).length ? fallbackMapping : this.getDefaultImportFallbackMapping(discoveredHeaders, activeMapping);
      this.setState({
        loading: false,
        importPreview: effectivePreviewRows,
        importRawPreview: rawPreviewRows,
        importHeaders: discoveredHeaders,
        importTotalRows: Number(data && data.totalRows ? data.totalRows : Math.max(previewRows.length, rawPreviewRows.length)),
        importDuplicateCount: Number(data && data.duplicateCount ? data.duplicateCount : 0),
        importMapping: activeMapping,
        importFallbackMapping: activeFallbackMapping,
      });
    }, () => {
      this.setState({ loading: false, importPreview: [], importTotalRows: 0, importDuplicateCount: 0 });
    });
  };

  handleImportFileChange = event => {
    const fileInput = event && event.target ? event.target : null;
    const file = fileInput && fileInput.files && fileInput.files[0];
    if (!file) {
      return;
    }
    if (!/\.(csv|xls|xlsx)$/i.test(file.name)) {
      window.alert(this.t("admin_local.customer_table.import_invalid", "Only CSV, XLS or XLSX files are supported."));
      if (fileInput) {
        fileInput.value = "";
      }
      return;
    }
    this.setState({ loading: true, importFile: file, importPreview: [], importRawPreview: [], importTotalRows: 0, importDuplicateCount: 0, importDuplicateAction: "skip", importMapping: {}, importFallbackMapping: {}, importHeaders: [] });
    admin.previewCustomers(file, null, { fallbackMapping: {} }, data => {
      const previewRows = Array.isArray(data?.sampleRows) ? data.sampleRows : [];
      const rawPreviewRows = Array.isArray(data?.rawRows) ? data.rawRows : [];
      const effectivePreviewRows = this.getImportPreviewRows(previewRows, rawPreviewRows);
      const discoveredHeaders = this.getImportPreviewHeaders(Array.isArray(data?.headers) ? data.headers : [], previewRows);
      const nextMapping = (data && data.defaultMapping && Object.keys(data.defaultMapping).length)
        ? this.resolveImportMapping(discoveredHeaders, data.defaultMapping)
        : this.getDefaultImportMapping(effectivePreviewRows);
      const nextFallbackMapping = this.getDefaultImportFallbackMapping(discoveredHeaders, nextMapping);
      this.setState({
        loading: false,
        importPreview: effectivePreviewRows,
        importRawPreview: rawPreviewRows,
        importHeaders: discoveredHeaders,
        importTotalRows: Number(data && data.totalRows ? data.totalRows : Math.max(previewRows.length, rawPreviewRows.length)),
        importDuplicateCount: Number(data && data.duplicateCount ? data.duplicateCount : 0),
        importDuplicateAction: "skip",
        importMapping: nextMapping,
        importFallbackMapping: nextFallbackMapping,
      });
      if (fileInput) {
        fileInput.value = "";
      }
    }, () => {
      this.setState({ loading: false, importPreview: [], importRawPreview: [], importTotalRows: 0, importDuplicateCount: 0, importDuplicateAction: "skip", importFile: null, importMapping: {}, importFallbackMapping: {}, importHeaders: [] });
      if (fileInput) {
        fileInput.value = "";
      }
    });
  };

  confirmImport = () => {
    const { importFile, importMapping, importFallbackMapping, importDuplicateAction } = this.state;
    if (!importFile) {
      return;
    }

    const progressState = {
      running: true,
      percent: 0,
      processed: 0,
      total: Math.max(1, Number(this.state.importTotalRows) || 1),
      imported: 0,
      updated: 0,
      skipped: 0,
      message: this.t("admin_local.customer_table.import_starting", "Starting import..."),
      error: "",
    };

    this.setState({ loading: true, importProgress: progressState });

    admin.importCustomersLongTermJob(importFile, importMapping, {
      duplicateAction: importDuplicateAction,
      fallbackMapping: importFallbackMapping,
      onProgress: event => {
        const percentValue = Number(event && (event.percent ?? event.progress ?? 0));
        const processedValue = Number(event && (event.processed ?? 0));
        const totalValue = Number(event && (event.total ?? Math.max(1, this.state.importProgress.total ?? 1)));
        const importedValue = Number(event && (event.imported ?? this.state.importProgress.imported ?? 0));
        const updatedValue = Number(event && (event.updated ?? this.state.importProgress.updated ?? 0));
        const skippedValue = Number(event && (event.skipped ?? this.state.importProgress.skipped ?? 0));
        const nextProgress = {
          running: true,
          percent: Number.isFinite(percentValue) ? percentValue : 0,
          processed: Number.isFinite(processedValue) ? processedValue : 0,
          total: Number.isFinite(totalValue) ? totalValue : Math.max(1, this.state.importProgress.total ?? 1),
          imported: Number.isFinite(importedValue) ? importedValue : this.state.importProgress.imported ?? 0,
          updated: Number.isFinite(updatedValue) ? updatedValue : this.state.importProgress.updated ?? 0,
          skipped: Number.isFinite(skippedValue) ? skippedValue : this.state.importProgress.skipped ?? 0,
          message: Number.isFinite(processedValue) && Number.isFinite(totalValue)
            ? this.t("admin_local.customer_table.import_progress", `Imported ${processedValue} / ${totalValue} rows`, processedValue, totalValue)
            : this.t("admin_local.customer_table.importing", "Importing..."),
          error: "",
        };
        this.setState({ importProgress: nextProgress });
      },
    }, data => {
      const imported = Number(data && data.imported ? data.imported : 0);
      const updated = Number(data && data.updated ? data.updated : 0);
      const skipped = Number(data && data.skipped ? data.skipped : 0);
      this.setState({
        loading: false,
        importFile: null,
        importPreview: [],
        importTotalRows: 0,
        importDuplicateCount: 0,
        importDuplicateAction: "skip",
        importMapping: {},
        importFallbackMapping: {},
        importProgress: {
          running: false,
          percent: 100,
          processed: this.state.importProgress.total || 0,
          total: this.state.importProgress.total || 0,
          imported,
          updated,
          skipped,
          message: this.t("admin_local.customer_table.import_summary", `Imported ${imported}, updated ${updated}, skipped ${skipped}`, imported, updated, skipped),
          error: "",
        },
      }, () => {
        this.loadCustomers();
      });
    }, error => {
      const message = error && error.message ? error.message : this.t("admin_local.customer_table.import_failed", "Import failed");
      this.setState({
        loading: false,
        importFile: null,
        importPreview: [],
        importTotalRows: 0,
        importDuplicateCount: 0,
        importDuplicateAction: "skip",
        importMapping: {},
        importFallbackMapping: {},
        importProgress: {
          running: false,
          percent: 0,
          processed: 0,
          total: 0,
          imported: 0,
          updated: 0,
          skipped: 0,
          message,
          error: message,
        },
      });
    });
  };

  render() {
    const { customers, loading, search, currentPage, pageSize, totalPages, totalElements, editingCustomerId, editingCustomer, importPreview, importRawPreview, importHeaders, importTotalRows, importDuplicateCount, importDuplicateAction, importFallbackMapping, importFile, importProgress } = this.state;
    const safeCurrentPage = Math.min(Math.max(currentPage, 1), Math.max(1, totalPages));
    const startIndex = (safeCurrentPage - 1) * pageSize;
    const showingFrom = customers.length === 0 ? 0 : startIndex + 1;
    const showingTo = Math.min(startIndex + pageSize, totalElements || customers.length);
    const hasPaging = (totalElements || customers.length) > pageSize;
    const mappingHeaders = this.getImportPreviewHeaders(importHeaders, importPreview);
    const mappedPreviewHeaders = importPreview && importPreview.length ? Object.keys(importPreview[0]) : [];

    return (
      <div style={{ display: "flex", flexFlow: "column", flexGrow: 1, overflow: "hidden", maxHeight: "100%", height: "100%", background: "#f3f3f3" }}>
        {!auth.isIncludedInFrame ? (
          <SubmenuHeader {...this.props} header={I18n.t("admin_local.menus.customers")} />
        ) : ""}

        <div style={{ display: "flex", flexDirection: "column", gap: "1rem", flex: 1, padding: "1rem", overflow: "hidden" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", padding: "1rem 1.25rem 0.75rem", borderBottom: "1px solid #e0e0e0", background: "#fff", borderRadius: "8px 8px 0 0", boxShadow: "0 1px 4px rgba(0,0,0,.08)" }}>
            <div>
              <div style={{ fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "#847556", fontWeight: 700 }}>
                {I18n.t("admin_local.menus.administration") || "Administration"}
              </div>
              <h3 style={{ margin: "0.2rem 0 0", fontSize: "1.6rem", color: "#2d2d2d" }}>{I18n.t("admin_local.menus.customers")}</h3>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <input
                type="text"
                value={search}
                onChange={this.handleSearchChange}
                placeholder={I18n.t("admin_local.customer_table.search") || "Search"}
                style={{ minWidth: "280px", padding: "0.65rem 0.9rem", borderRadius: "6px", border: "1px solid #d8d8d8", background: "#fafafa", boxShadow: "inset 0 1px 2px rgba(0,0,0,.04)" }}
              />
              <label className="btn btn-sm btn-primary" htmlFor="customer-import-file" style={{ margin: 0, cursor: "pointer" }}>
                {I18n.t("admin_local.customer_table.import") || "Import"}
              </label>
              <input id="customer-import-file" type="file" accept=".csv,.xls,.xlsx" onChange={this.handleImportFileChange} style={{ display: "none" }} />
            </div>
          </div>

          {importFile && (importPreview.length > 0 || importRawPreview.length > 0) && (
            <div style={{ background: "#fff9d9", border: "1px solid #f0d98d", padding: "0.85rem 1rem", margin: "0 1rem", borderRadius: "8px", overflow: "hidden", display: "flex", flexDirection: "column", height: "100%", minHeight: 0, maxHeight: "100%", flex: 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", marginBottom: "0.75rem" }}>
                <strong>{I18n.t("admin_local.customer_table.import_preview") || "Import preview"}</strong>
                <span>{I18n.t("admin_local.customer_table.preview_rows", importTotalRows) || `Previewing ${importTotalRows} rows`}</span>
              </div>

              {importProgress.running && (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "0.75rem", background: "#f2f8ff", border: "1px solid #cfe3ff", borderRadius: "6px", padding: "0.75rem 0.9rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                      <strong style={{ color: "#18427a" }}>{importProgress.message || this.t("admin_local.customer_table.importing_customers", "Importing customers...")}</strong>
                    <span style={{ color: "#18427a", fontWeight: 600 }}>{importProgress.percent}%</span>
                  </div>
                  <div style={{ width: "100%", height: "10px", borderRadius: "999px", background: "#dfeefe", overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, Number(importProgress.percent) || 0)}%`, height: "100%", background: "linear-gradient(90deg, #1d7ae8, #5aa8ff)" }} />
                  </div>
                  <div style={{ display: "flex", gap: "1rem", color: "#18427a", fontSize: "0.8rem", flexWrap: "wrap" }}>
                    <span>{this.t("admin_local.customer_table.processed", "Processed")}: {importProgress.processed}/{importProgress.total}</span>
                    <span>{this.t("admin_local.customer_table.imported", "Imported")}: {importProgress.imported}</span>
                    <span>{this.t("admin_local.customer_table.updated", "Updated")}: {importProgress.updated}</span>
                    <span>{this.t("admin_local.customer_table.skipped", "Skipped")}: {importProgress.skipped}</span>
                  </div>
                </div>
              )}

              {importDuplicateCount > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem", background: "#fff3cd", border: "1px solid #f0d98d", borderRadius: "6px", padding: "0.55rem 0.8rem" }}>
                  <strong style={{ color: "#5b4a21" }}>{this.t("admin_local.customer_table.duplicates_found", `Duplicate customers found: ${importDuplicateCount}`, importDuplicateCount)}</strong>
                  <select
                    value={importDuplicateAction}
                    onChange={event => this.setState({ importDuplicateAction: event.target.value })}
                    style={{ padding: "0.35rem 0.5rem", borderRadius: "4px", border: "1px solid #d7c77c", minWidth: "220px" }}
                  >
                    <option value="skip">{this.t("admin_local.customer_table.duplicate_action_skip_rows", "Skip duplicate rows")}</option>
                    <option value="update">{this.t("admin_local.customer_table.duplicate_action_update_records", "Update existing records")}</option>
                    <option value="new">{this.t("admin_local.customer_table.duplicate_action_only_new", "Import only new records")}</option>
                  </select>
                </div>
              )}

              <div style={{ display: "flex", flexWrap: "nowrap", overflowX: "auto", gap: "0.75rem", marginBottom: "0.75rem", paddingBottom: "0.2rem" }}>
                {this.getImportTargetOptions().map(option => {
                  const targetField = option.value;
                  const sourceHeader = this.state.importMapping[targetField] || "";
                  const sourceOptions = this.getImportSourceOptions(mappingHeaders);
                  const fallbackOptions = sourceOptions.filter(candidate => candidate !== sourceHeader);
                  const fallbackValue = importFallbackMapping[targetField] || "";

                  return (
                    <div key={`mapping-${targetField}`} style={{ display: "flex", flexDirection: "column", minWidth: "180px", gap: "0.35rem" }}>
                      <div style={{ fontWeight: 600, color: "#584b2a", fontSize: "0.8rem", lineHeight: 1.2 }}>{option.label}</div>
                      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#847556" }}>{this.t("admin_local.customer_table.primary", "Primary")}</div>
                      <select
                        value={sourceHeader}
                        onChange={event => this.handleImportMappingChange(targetField, event.target.value)}
                        style={{ padding: "0.3rem 0.45rem", borderRadius: "4px", border: "1px solid #d7c77c", fontSize: "0.8rem" }}
                      >
                        <option value="">{this.t("admin_local.customer_table.ignore", "Ignore")}</option>
                        {sourceOptions.map(header => (
                          <option key={`target-${targetField}-${header}`} value={header}>{this.getImportHeaderLabel(header)}</option>
                        ))}
                      </select>
                      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#847556" }}>{this.t("admin_local.customer_table.fallback", "Fallback")}</div>
                      <select
                        value={fallbackValue}
                        onChange={event => this.handleImportFallbackMappingChange(targetField, event.target.value)}
                        disabled={!sourceHeader}
                        style={{ padding: "0.3rem 0.45rem", borderRadius: "4px", border: "1px solid #d7c77c", fontSize: "0.8rem", color: !sourceHeader ? "#999" : "#1d1d1d" }}
                      >
                        <option value="">{this.t("admin_local.customer_table.primary_only", "Primary only")}</option>
                        {fallbackOptions.map(header => (
                          <option key={`fallback-${targetField}-${header}`} value={header}>{this.getImportHeaderLabel(header)}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginBottom: "0.75rem", width: "100%", overflow: "hidden", minHeight: 0, flex: 1 }}>
                <div style={{ width: "100%", overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: "0.35rem", color: "#584b2a" }}>{this.t("admin_local.customer_table.original_excel", "Original Excel")}</div>
                  <div style={{ flex: 1, minHeight: 0, height: "100%", overflow: "auto", border: "1px solid #e8d38d", borderRadius: "6px", width: "100%", maxWidth: "100%" }}>
                    <table style={{ width: "max-content", minWidth: 0, borderCollapse: "collapse", fontSize: "0.85rem" }}>
                      <thead>
                        <tr style={{ background: "#f5f0d7", position: "sticky", top: 0, zIndex: 1 }}>
                          {mappingHeaders.map(header => (
                            <th key={`orig-${header}`} style={{ padding: "0.35rem 0.5rem", borderBottom: "1px solid #e8d38d", textAlign: "left" }}>{this.getImportHeaderLabel(header)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(importRawPreview.length ? importRawPreview : importPreview).slice(0, 100).map((row, index) => (
                          <tr key={`orig-row-${index}-${mappingHeaders.join('-')}`}>
                            {mappingHeaders.map(header => (
                              <td key={`orig-cell-${index}-${header}`} style={{ padding: "0.3rem 0.5rem", borderBottom: "1px solid #f2e7ba" }}>
                                {row && row[header] !== undefined && row[header] !== null ? String(row[header]) : "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div style={{ width: "100%", overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: "0.35rem", color: "#584b2a" }}>{this.t("admin_local.customer_table.after_mapping", "After mapping")}</div>
                  <div style={{ flex: 1, minHeight: 0, height: "100%", overflow: "auto", border: "1px solid #e8d38d", borderRadius: "6px", width: "100%", maxWidth: "100%" }}>
                    <table style={{ width: "100%", maxWidth: "100%", minWidth: 0, borderCollapse: "collapse", fontSize: "0.85rem" }}>
                      <thead>
                        <tr style={{ background: "#f5f0d7", position: "sticky", top: 0, zIndex: 1 }}>
                          {mappedPreviewHeaders.map(header => (
                            <th key={`mapped-${header}`} style={{ padding: "0.35rem 0.5rem", borderBottom: "1px solid #e8d38d", textAlign: "left" }}>{this.getImportHeaderLabel(header)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.slice(0, 100).map((row, index) => (
                          <tr key={`mapped-row-${index}-${mappedPreviewHeaders.join('-')}`}>
                            {mappedPreviewHeaders.map(header => (
                              <td key={`mapped-cell-${index}-${header}`} style={{ padding: "0.3rem 0.5rem", borderBottom: "1px solid #f2e7ba" }}>
                                {row && row[header] !== undefined && row[header] !== null ? String(row[header]) : "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: "0.75rem", display: "flex", justifyContent: "flex-end" }}>
                <button type="button" className="btn btn-success btn-sm" onClick={this.confirmImport}>
                  {this.t("admin_local.customer_table.import_confirm", "Import now")}
                </button>
              </div>
            </div>
          )}

          {!importFile && (
            <div style={{ background: "white", borderRadius: "8px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.08)", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div style={{ overflow: "auto", flex: 1, minHeight: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6" }}>{I18n.t("admin_local.customer_table.name") || "Name"}</th>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6" }}>{I18n.t("admin_local.customer_table.email") || "Email"}</th>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6" }}>{I18n.t("admin_local.customer_table.phone") || "Phone"}</th>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6" }}>{I18n.t("admin_local.customer_table.address") || "Address"}</th>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6" }}>{I18n.t("admin_local.customer_table.city") || "City"}</th>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6" }}>{I18n.t("admin_local.customer_table.postcode") || "Postcode"}</th>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6" }}>{I18n.t("admin_local.customer_table.country") || "Country"}</th>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6" }}>{I18n.t("admin_local.customer_table.labels") || "Labels"}</th>
                      <th style={{ padding: "0.85rem 1rem", borderBottom: "1px solid #e6e6e6", textAlign: "center" }}>{I18n.t("admin_local.customer_table.delete") || "Delete"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} style={{ padding: "1.25rem", textAlign: "center", color: "#666" }}>{I18n.t("admin_local.customer_table.loading") || "Loading..."}</td>
                      </tr>
                    ) : customers.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ padding: "1.25rem", textAlign: "center", color: "#666" }}>{I18n.t("admin_local.customer_table.no_results") || "No results"}</td>
                      </tr>
                    ) : (
                      customers.map(customer => {
                        const isEditing = editingCustomerId === customer.id;
                        if (isEditing && editingCustomer) {
                          return (
                            <tr key={customer.id} style={{ borderTop: "1px solid #eee" }}>
                              <td style={{ padding: "0.5rem 0.75rem" }}><input name="name" value={editingCustomer.name || ""} onChange={this.handleEditFieldChange} style={{ width: "100%", padding: "0.35rem 0.5rem" }} /></td>
                              <td style={{ padding: "0.5rem 0.75rem" }}><input name="email" value={editingCustomer.email || ""} onChange={this.handleEditFieldChange} style={{ width: "100%", padding: "0.35rem 0.5rem" }} /></td>
                              <td style={{ padding: "0.5rem 0.75rem" }}><input name="phone" value={editingCustomer.phone || ""} onChange={this.handleEditFieldChange} style={{ width: "100%", padding: "0.35rem 0.5rem" }} /></td>
                              <td style={{ padding: "0.5rem 0.75rem" }}><input name="address" value={editingCustomer.address || ""} onChange={this.handleEditFieldChange} style={{ width: "100%", padding: "0.35rem 0.5rem" }} /></td>
                              <td style={{ padding: "0.5rem 0.75rem" }}><input name="city" value={editingCustomer.city || ""} onChange={this.handleEditFieldChange} style={{ width: "100%", padding: "0.35rem 0.5rem" }} /></td>
                              <td style={{ padding: "0.5rem 0.75rem" }}><input name="postcode" value={editingCustomer.postcode || ""} onChange={this.handleEditFieldChange} style={{ width: "100%", padding: "0.35rem 0.5rem" }} /></td>
                              <td style={{ padding: "0.5rem 0.75rem" }}><input name="country" value={editingCustomer.country || ""} onChange={this.handleEditFieldChange} style={{ width: "100%", padding: "0.35rem 0.5rem" }} /></td>
                              <td style={{ padding: "0.5rem 0.75rem" }}>{this.getLabels(customer)}</td>
                              <td style={{ padding: "0.5rem 0.75rem", textAlign: "center" }}>
                                <div style={{ display: "flex", gap: "0.35rem", justifyContent: "center" }}>
                                  <button type="button" className="btn btn-success btn-sm" onClick={this.saveCustomerEdit}>{this.t("admin_local.customer_table.save", "Save")}</button>
                                  <button type="button" className="btn btn-light btn-sm" onClick={this.cancelEditCustomer}>{this.t("admin_local.customer_table.cancel", "Cancel")}</button>
                                </div>
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={customer.id} style={{ borderTop: "1px solid #eee" }}>
                            <td style={{ padding: "0.85rem 1rem" }}>{[customer.name, customer.surname].filter(Boolean).join(" ") || "-"}</td>
                            <td style={{ padding: "0.85rem 1rem" }}>{customer.email || "-"}</td>
                            <td style={{ padding: "0.85rem 1rem" }}>{this.getPhone(customer)}</td>
                            <td style={{ padding: "0.85rem 1rem" }}>{this.getStreetAddress(customer)}</td>
                            <td style={{ padding: "0.85rem 1rem" }}>{this.getCity(customer)}</td>
                            <td style={{ padding: "0.85rem 1rem" }}>{this.getPostcode(customer)}</td>
                            <td style={{ padding: "0.85rem 1rem" }}>{this.getCountry(customer)}</td>
                            <td style={{ padding: "0.85rem 1rem" }}>{this.getLabels(customer)}</td>
                            <td style={{ padding: "0.85rem 1rem", textAlign: "center" }}>
                              <div style={{ display: "flex", gap: "0.35rem", justifyContent: "center" }}>
                                <button type="button" className="btn btn-primary btn-sm" onClick={() => this.startEditCustomer(customer)}>
                                  {this.t("admin_local.customer_table.edit", "Edit")}
                                </button>
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => this.handleDelete(customer)}>
                                  {I18n.t("admin_local.customer_table.delete") || "Delete"}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && hasPaging && (
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", padding: "0.85rem 1rem", borderTop: "1px solid #e6e6e6", background: "#fafafa", flexShrink: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ color: "#4b4b4b" }}>{I18n.t("admin_local.customer_table.items_per_page") || "Per page"}</span>
                    <select
                      value={pageSize}
                      onChange={this.handlePageSizeChange}
                      style={{ minWidth: "80px", padding: "0.35rem 0.5rem", borderRadius: "4px", border: "1px solid #d8d8d8", background: "#fff" }}
                    >
                      {[10, 20, 50, 100].map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ color: "#4b4b4b" }}>
                    {I18n.t("admin_local.customer_table.showing") || "Showing"} {showingFrom} - {showingTo} {I18n.t("admin_local.customer_table.of") || "of"} {totalElements}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => this.handlePageChange(safeCurrentPage - 1)}
                      disabled={safeCurrentPage === 1}
                    >
                      {I18n.t("admin_local.customer_table.previous") || "Previous"}
                    </button>
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNum = index + 1;
                      const isActive = pageNum === safeCurrentPage;
                      return (
                        <button
                          key={pageNum}
                          type="button"
                          className={`btn btn-sm ${isActive ? "btn-primary" : "btn-light"}`}
                          onClick={() => this.handlePageChange(pageNum)}
                          style={{ minWidth: "36px" }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => this.handlePageChange(safeCurrentPage + 1)}
                      disabled={safeCurrentPage === totalPages}
                    >
                      {I18n.t("admin_local.customer_table.next") || "Next"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {!auth.isIncludedInFrame ? <AICommandBox /> : ""}
      </div>
    );
  }
}

export { Customers };

const mapStateToProps = state => ({
  myStatus: state.rootReducer.myStatus,
});

export default connect(mapStateToProps)(Customers);
